import { useState, useEffect, useCallback } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  LinearProgress,
  Chip,
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  PlayArrow,
  Stop,
  Refresh,
  TrendingUp,
  People,
  Explore,
  Groups,
  Schedule,
  FilterList,
  Settings,
  DateRange,
  History,
} from '@mui/icons-material'
import { statisticsApi } from '@api/endpoints/statistics'
import {
  StatisticsJobStatusDto,
  StatisticsJobDto,
  DailyUserStatisticsDto,
  DailyAdventureStatisticsDto,
  DailyGroupStatisticsDto,
  UserStatisticsSortBy,
  AdventureStatisticsSortBy,
} from '@api/types/statistics'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stats-tabpanel-${index}`}
      aria-labelledby={`stats-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )
}

export default function StatisticsPage() {
  usePageTitle('Statistics')
  // Job status state
  const [jobStatus, setJobStatus] = useState<StatisticsJobStatusDto | null>(null)
  const [jobLoading, setJobLoading] = useState(true)
  const [jobError, setJobError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [stopping, setStopping] = useState(false)

  // Rebuild options
  const [includeUsers, setIncludeUsers] = useState(true)
  const [includeAdventures, setIncludeAdventures] = useState(true)
  const [includeGroups, setIncludeGroups] = useState(true)
  const [rebuildStartDate, setRebuildStartDate] = useState('')
  const [rebuildEndDate, setRebuildEndDate] = useState('')
  const [showRebuildOptions, setShowRebuildOptions] = useState(false)

  // Statistics date range
  const [statsStartDate, setStatsStartDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0]
  })
  const [statsEndDate, setStatsEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0]
  })

  // Statistics data
  const [userStats, setUserStats] = useState<DailyUserStatisticsDto[]>([])
  const [adventureStats, setAdventureStats] = useState<DailyAdventureStatisticsDto[]>([])
  const [groupStats, setGroupStats] = useState<DailyGroupStatisticsDto[]>([])
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsError, setStatsError] = useState<string | null>(null)

  // User stats pagination and sorting
  const [userPage, setUserPage] = useState(0)
  const [userPageSize, setUserPageSize] = useState(25)
  const [userTotalCount, setUserTotalCount] = useState(0)
  const [userSortBy, setUserSortBy] = useState<UserStatisticsSortBy>('Date')
  const [userSortDesc, setUserSortDesc] = useState(true)

  // Adventure stats pagination and sorting
  const [adventurePage, setAdventurePage] = useState(0)
  const [adventurePageSize, setAdventurePageSize] = useState(25)
  const [adventureTotalCount, setAdventureTotalCount] = useState(0)
  const [adventureSortBy, setAdventureSortBy] = useState<AdventureStatisticsSortBy>('Date')
  const [adventureSortDesc, setAdventureSortDesc] = useState(true)

  // Group stats pagination and sorting
  const [groupPage, setGroupPage] = useState(0)
  const [groupPageSize, setGroupPageSize] = useState(25)
  const [groupTotalCount, setGroupTotalCount] = useState(0)
  const [groupSortDesc, setGroupSortDesc] = useState(true)

  // Tab state
  const [activeTab, setActiveTab] = useState(0)

  // Adventure category filter
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>(undefined)
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)

  // Dialog state
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false)
  const [jobDialogOpen, setJobDialogOpen] = useState(false)

  // Activity logs (job history)
  const [logs, setLogs] = useState<StatisticsJobDto[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsPage, setLogsPage] = useState(0)
  const [logsPageSize, setLogsPageSize] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)

  // Fetch job status
  const fetchJobStatus = useCallback(async () => {
    try {
      const status = await statisticsApi.getStatus()
      setJobStatus(status)
      setJobError(null)
    } catch (err) {
      setJobError(err instanceof Error ? err.message : 'Failed to fetch job status')
    } finally {
      setJobLoading(false)
    }
  }, [])

  // Fetch user statistics
  const fetchUserStatistics = useCallback(async () => {
    setStatsLoading(true)
    setStatsError(null)
    try {
      const response = await statisticsApi.getUserStatistics({
        startDate: statsStartDate || undefined,
        endDate: statsEndDate || undefined,
        page: userPage + 1,
        pageSize: userPageSize,
        sortBy: userSortBy,
        sortDescending: userSortDesc,
      })
      setUserStats(response.items || [])
      setUserTotalCount(response.totalCount || 0)
    } catch (err) {
      setStatsError(err instanceof Error ? err.message : 'Failed to fetch user statistics')
    } finally {
      setStatsLoading(false)
    }
  }, [statsStartDate, statsEndDate, userPage, userPageSize, userSortBy, userSortDesc])

  // Fetch adventure statistics
  const fetchAdventureStatistics = useCallback(async () => {
    setStatsLoading(true)
    setStatsError(null)
    try {
      const response = await statisticsApi.getAdventureStatistics({
        startDate: statsStartDate || undefined,
        endDate: statsEndDate || undefined,
        category: categoryFilter,
        page: adventurePage + 1,
        pageSize: adventurePageSize,
        sortBy: adventureSortBy,
        sortDescending: adventureSortDesc,
      })
      setAdventureStats(response.items || [])
      setAdventureTotalCount(response.totalCount || 0)
    } catch (err) {
      setStatsError(err instanceof Error ? err.message : 'Failed to fetch adventure statistics')
    } finally {
      setStatsLoading(false)
    }
  }, [statsStartDate, statsEndDate, categoryFilter, adventurePage, adventurePageSize, adventureSortBy, adventureSortDesc])

  // Fetch group statistics
  const fetchGroupStatistics = useCallback(async () => {
    setStatsLoading(true)
    try {
      const response = await statisticsApi.getGroupStatistics({
        startDate: statsStartDate || undefined,
        endDate: statsEndDate || undefined,
        page: groupPage + 1,
        pageSize: groupPageSize,
        sortDescending: groupSortDesc,
      })
      setGroupStats(response.items || [])
      setGroupTotalCount(response.totalCount || 0)
    } catch (err) {
      console.error('Failed to fetch group statistics:', err)
    } finally {
      setStatsLoading(false)
    }
  }, [statsStartDate, statsEndDate, groupPage, groupPageSize, groupSortDesc])

  // Fetch all statistics
  const fetchAllStatistics = useCallback(() => {
    fetchUserStatistics()
    fetchAdventureStatistics()
    fetchGroupStatistics()
  }, [fetchUserStatistics, fetchAdventureStatistics, fetchGroupStatistics])

  // Fetch activity logs
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true)
    try {
      const response = await statisticsApi.getLogs({
        page: logsPage + 1,
        pageSize: logsPageSize,
      })
      setLogs(response?.logs || [])
      setLogsTotalCount(response?.totalCount || 0)
    } catch (err) {
      console.error('Failed to fetch logs:', err)
      setLogs([])
    } finally {
      setLogsLoading(false)
    }
  }, [logsPage, logsPageSize])

  // Start rebuild
  const handleStartRebuild = async () => {
    setStarting(true)
    setJobError(null)
    try {
      await statisticsApi.startRebuild({
        includeUsers,
        includeAdventures,
        includeGroups,
        startDate: rebuildStartDate || undefined,
        endDate: rebuildEndDate || undefined,
      })
      await fetchJobStatus()
    } catch (err) {
      setJobError(err instanceof Error ? err.message : 'Failed to start rebuild')
    } finally {
      setStarting(false)
    }
  }

  // Stop rebuild
  const handleStopRebuild = async () => {
    setStopping(true)
    setJobError(null)
    try {
      await statisticsApi.stopRebuild()
      await fetchJobStatus()
    } catch (err) {
      setJobError(err instanceof Error ? err.message : 'Failed to stop rebuild')
    } finally {
      setStopping(false)
    }
  }

  // Initial job status load (only once)
  useEffect(() => {
    fetchJobStatus()
  }, [fetchJobStatus])

  // Fetch user stats when pagination/sorting/date changes
  useEffect(() => {
    fetchUserStatistics()
  }, [fetchUserStatistics])

  // Fetch adventure stats when pagination/sorting/filter/date changes
  useEffect(() => {
    fetchAdventureStatistics()
  }, [fetchAdventureStatistics])

  // Fetch group stats when pagination/sorting/date changes
  useEffect(() => {
    fetchGroupStatistics()
  }, [fetchGroupStatistics])

  // Handle user sort change
  const handleUserSortChange = (column: UserStatisticsSortBy) => {
    if (userSortBy === column) {
      setUserSortDesc(!userSortDesc)
    } else {
      setUserSortBy(column)
      setUserSortDesc(true)
    }
    setUserPage(0)
  }

  // Handle adventure sort change
  const handleAdventureSortChange = (column: AdventureStatisticsSortBy) => {
    if (adventureSortBy === column) {
      setAdventureSortDesc(!adventureSortDesc)
    } else {
      setAdventureSortBy(column)
      setAdventureSortDesc(true)
    }
    setAdventurePage(0)
  }

  // Handle group sort change (Date only)
  const handleGroupSortToggle = () => {
    setGroupSortDesc(!groupSortDesc)
    setGroupPage(0)
  }

  // Fetch logs when Job History tab is active
  useEffect(() => {
    if (activeTab === 3) {
      fetchLogs()
    }
  }, [activeTab, fetchLogs])

  // Auto-refresh job status when running
  useEffect(() => {
    if (jobStatus?.isRunning) {
      const interval = setInterval(fetchJobStatus, 5000)
      return () => clearInterval(interval)
    }
  }, [jobStatus?.isRunning, fetchJobStatus])

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString()
  }

  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString()
  }

  // Calculate summary stats
  const latestUserStats = userStats[userStats.length - 1]

  const totalNewUsers = userStats.reduce((sum, s) => sum + s.newUsersCount, 0)
  const totalNewAdventures = adventureStats.reduce((sum, s) => sum + s.newAdventuresCount, 0)
  const totalSoloCompleted = adventureStats.reduce((sum, s) => sum + s.soloSessionsCompleted, 0)
  const totalGroupCompleted = adventureStats.reduce((sum, s) => sum + s.groupSessionsCompleted, 0)
  const totalCompletions = totalSoloCompleted + totalGroupCompleted

  // Category options for filter
  const categoryOptions: { value: number | undefined; label: string }[] = [
    { value: undefined, label: 'All Categories' },
    { value: 1, label: 'Nature' },
    { value: 2, label: 'Urban' },
    { value: 4, label: 'Historical' },
    { value: 7, label: 'Educational' },
    { value: 9, label: 'Other' },
  ]

  // Get category name for display
  const getCategoryName = (categoryId: number) => {
    const option = categoryOptions.find((o) => o.value === categoryId)
    return option?.label || `Category ${categoryId}`
  }

  // Count days with group activity
  const daysWithGroupActivity = groupStats.filter(
    (s) => s.topGroupsByActiveMembers?.length || s.topGroupsByPosts?.length
  ).length

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {statsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatsError(null)}>
          {statsError}
        </Alert>
      )}

      {jobError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobError(null)}>
          {jobError}
        </Alert>
      )}

      {/* Summary Cards - Auto-fit layout */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          '& > *': {
            flex: '1 1 200px',
            minWidth: '180px',
            maxWidth: '100%',
          },
        }}
      >
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <People color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                Total Users
              </Typography>
            </Box>
            <Typography variant="h4">
              {latestUserStats?.totalUsersCount ?? '-'}
            </Typography>
            <Typography variant="body2" color="success.main">
              +{totalNewUsers} new in period
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Explore color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                New Adventures
              </Typography>
            </Box>
            <Typography variant="h4">
              {totalNewAdventures}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              in selected period
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TrendingUp color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                Sessions Completed
              </Typography>
            </Box>
            <Typography variant="h4">
              {totalCompletions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalSoloCompleted} solo, {totalGroupCompleted} group
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Groups color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                Group Activity
              </Typography>
            </Box>
            <Typography variant="h4">
              {daysWithGroupActivity}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              days with activity
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Statistics Tables with Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          {/* Date Range Icon - Left */}
          <Tooltip title={`${statsStartDate} to ${statsEndDate}`}>
            <IconButton
              onClick={() => setDateRangeDialogOpen(true)}
              sx={{ ml: 1 }}
            >
              <DateRange />
            </IconButton>
          </Tooltip>

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="statistics tabs"
            sx={{ flex: 1 }}
          >
            <Tab icon={<People />} iconPosition="start" label="Users" />
            <Tab icon={<Explore />} iconPosition="start" label="Adventures" />
            <Tab icon={<Groups />} iconPosition="start" label="Groups" />
            <Tab icon={<History />} iconPosition="start" label="Job History" />
          </Tabs>

          {/* Category Filter - only on Adventures tab */}
          {activeTab === 1 && (
            <Tooltip title={categoryFilter === undefined ? 'Filter by category' : getCategoryName(categoryFilter)}>
              <IconButton
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                color={categoryFilter !== undefined ? 'primary' : 'default'}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
          )}

          {/* Refresh Icon */}
          <Tooltip title="Refresh data">
            <IconButton
              onClick={() => {
                fetchJobStatus()
                fetchAllStatistics()
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>

          {/* Settings/Job Dialog Icon */}
          <Tooltip title="Rebuild job settings">
            <IconButton
              onClick={() => setJobDialogOpen(true)}
              sx={{ mr: 1 }}
              color={jobStatus?.isRunning ? 'success' : 'default'}
            >
              <Settings />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={() => setFilterAnchorEl(null)}
          >
            {categoryOptions.map((opt) => (
              <MenuItem
                key={opt.value ?? 'all'}
                selected={categoryFilter === opt.value}
                onClick={() => {
                  setCategoryFilter(opt.value)
                  setAdventurePage(0)
                  setFilterAnchorEl(null)
                }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* User Statistics Tab */}
        <TabPanel value={activeTab} index={0}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={userSortBy === 'Date'}
                      direction={userSortBy === 'Date' ? (userSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleUserSortChange('Date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={userSortBy === 'NewUsers'}
                      direction={userSortBy === 'NewUsers' ? (userSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleUserSortChange('NewUsers')}
                    >
                      New
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={userSortBy === 'ActiveUsers'}
                      direction={userSortBy === 'ActiveUsers' ? (userSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleUserSortChange('ActiveUsers')}
                    >
                      Active
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={userSortBy === 'Subscriptions'}
                      direction={userSortBy === 'Subscriptions' ? (userSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleUserSortChange('Subscriptions')}
                    >
                      Subscriptions
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={userSortBy === 'Total'}
                      direction={userSortBy === 'Total' ? (userSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleUserSortChange('Total')}
                    >
                      Total
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : userStats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  userStats.map((stat) => (
                    <TableRow key={stat.statisticsDate} hover>
                      <TableCell>{formatShortDate(stat.statisticsDate)}</TableCell>
                      <TableCell align="right">{stat.newUsersCount}</TableCell>
                      <TableCell align="right">{stat.activeUsersCount}</TableCell>
                      <TableCell align="right">{stat.activeSubscriptionsCount}</TableCell>
                      <TableCell align="right">{stat.totalUsersCount}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={userTotalCount}
            rowsPerPage={userPageSize}
            page={userPage}
            onPageChange={(_, newPage) => setUserPage(newPage)}
            onRowsPerPageChange={(e) => {
              setUserPageSize(parseInt(e.target.value, 10))
              setUserPage(0)
            }}
          />
        </TabPanel>

        {/* Adventure Statistics Tab */}
        <TabPanel value={activeTab} index={1}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={adventureSortBy === 'Date'}
                      direction={adventureSortBy === 'Date' ? (adventureSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleAdventureSortChange('Date')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  {categoryFilter === undefined && (
                    <TableCell>
                      <TableSortLabel
                        active={adventureSortBy === 'Category'}
                        direction={adventureSortBy === 'Category' ? (adventureSortDesc ? 'desc' : 'asc') : 'asc'}
                        onClick={() => handleAdventureSortChange('Category')}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <TableSortLabel
                      active={adventureSortBy === 'NewAdventures'}
                      direction={adventureSortBy === 'NewAdventures' ? (adventureSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleAdventureSortChange('NewAdventures')}
                    >
                      New
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={adventureSortBy === 'SoloStarted'}
                      direction={adventureSortBy === 'SoloStarted' ? (adventureSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleAdventureSortChange('SoloStarted')}
                    >
                      Solo Started
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={adventureSortBy === 'SoloCompleted'}
                      direction={adventureSortBy === 'SoloCompleted' ? (adventureSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleAdventureSortChange('SoloCompleted')}
                    >
                      Solo Completed
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={adventureSortBy === 'GroupStarted'}
                      direction={adventureSortBy === 'GroupStarted' ? (adventureSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleAdventureSortChange('GroupStarted')}
                    >
                      Group Started
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={adventureSortBy === 'GroupCompleted'}
                      direction={adventureSortBy === 'GroupCompleted' ? (adventureSortDesc ? 'desc' : 'asc') : 'asc'}
                      onClick={() => handleAdventureSortChange('GroupCompleted')}
                    >
                      Group Completed
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statsLoading ? (
                  <TableRow>
                    <TableCell colSpan={categoryFilter === undefined ? 7 : 6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : adventureStats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={categoryFilter === undefined ? 7 : 6} align="center" sx={{ py: 4 }}>
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  adventureStats.map((stat, idx) => (
                    <TableRow key={`${stat.statisticsDate}-${stat.mainCategory}-${idx}`} hover>
                      <TableCell>{formatShortDate(stat.statisticsDate)}</TableCell>
                      {categoryFilter === undefined && (
                        <TableCell>
                          <Chip
                            label={getCategoryName(stat.mainCategory)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell align="right">{stat.newAdventuresCount}</TableCell>
                      <TableCell align="right">{stat.soloSessionsStarted}</TableCell>
                      <TableCell align="right">{stat.soloSessionsCompleted}</TableCell>
                      <TableCell align="right">{stat.groupSessionsStarted}</TableCell>
                      <TableCell align="right">{stat.groupSessionsCompleted}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={adventureTotalCount}
            rowsPerPage={adventurePageSize}
            page={adventurePage}
            onPageChange={(_, newPage) => setAdventurePage(newPage)}
            onRowsPerPageChange={(e) => {
              setAdventurePageSize(parseInt(e.target.value, 10))
              setAdventurePage(0)
            }}
          />
        </TabPanel>

        {/* Group Statistics Tab */}
        <TabPanel value={activeTab} index={2}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={true}
                      direction={groupSortDesc ? 'desc' : 'asc'}
                      onClick={handleGroupSortToggle}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Top by Active Members</TableCell>
                  <TableCell>Top by Posts</TableCell>
                  <TableCell>Top by Events</TableCell>
                  <TableCell>Top by Adventures</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : groupStats.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  groupStats.map((stat) => (
                    <TableRow key={stat.statisticsDate} hover>
                      <TableCell>{formatShortDate(stat.statisticsDate)}</TableCell>
                      <TableCell>
                        {stat.topGroupsByActiveMembers?.length ? (
                          stat.topGroupsByActiveMembers.slice(0, 2).map((g) => (
                            <Chip
                              key={g.groupId}
                              label={`${g.groupName} (${g.count})`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {stat.topGroupsByPosts?.length ? (
                          stat.topGroupsByPosts.slice(0, 2).map((g) => (
                            <Chip
                              key={g.groupId}
                              label={`${g.groupName} (${g.count})`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {stat.topGroupsByEvents?.length ? (
                          stat.topGroupsByEvents.slice(0, 2).map((g) => (
                            <Chip
                              key={g.groupId}
                              label={`${g.groupName} (${g.count})`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {stat.topGroupsByAdventures?.length ? (
                          stat.topGroupsByAdventures.slice(0, 2).map((g) => (
                            <Chip
                              key={g.groupId}
                              label={`${g.groupName} (${g.count})`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={groupTotalCount}
            rowsPerPage={groupPageSize}
            page={groupPage}
            onPageChange={(_, newPage) => setGroupPage(newPage)}
            onRowsPerPageChange={(e) => {
              setGroupPageSize(parseInt(e.target.value, 10))
              setGroupPage(0)
            }}
          />
        </TabPanel>

        {/* Job History Tab */}
        <TabPanel value={activeTab} index={3}>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Started</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Records</TableCell>
                  <TableCell>Date Range</TableCell>
                  <TableCell>Triggered By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      No job history available
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((job) => (
                    <TableRow key={job.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(job.startedAt)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            job.status === 0 ? 'Pending' :
                            job.status === 1 ? 'Running' :
                            job.status === 2 ? 'Cancelled' :
                            job.status === 3 ? 'Completed' :
                            job.status === 4 ? 'Failed' :
                            'Unknown'
                          }
                          size="small"
                          color={
                            job.status === 3 ? 'success' :
                            job.status === 4 ? 'error' :
                            job.status === 1 ? 'info' :
                            job.status === 2 ? 'warning' :
                            'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{job.duration || '-'}</TableCell>
                      <TableCell align="right">{job.recordsProcessed.toLocaleString()}</TableCell>
                      <TableCell>
                        {job.dateRangeStart && job.dateRangeEnd
                          ? `${job.dateRangeStart} to ${job.dateRangeEnd}`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={job.triggeredByUserId || ''}>
                          <Typography variant="body2" noWrap>
                            {job.triggeredByUserName || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={logsTotalCount}
            rowsPerPage={logsPageSize}
            page={logsPage}
            onPageChange={(_, newPage) => setLogsPage(newPage)}
            onRowsPerPageChange={(e) => {
              setLogsPageSize(parseInt(e.target.value, 10))
              setLogsPage(0)
            }}
          />
        </TabPanel>
      </Paper>

      {/* Date Range Dialog */}
      <Dialog open={dateRangeDialogOpen} onClose={() => setDateRangeDialogOpen(false)}>
        <DialogTitle>View Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 280 }}>
            <TextField
              type="date"
              label="From"
              value={statsStartDate}
              onChange={(e) => setStatsStartDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              value={statsEndDate}
              onChange={(e) => setStatsEndDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateRangeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setUserPage(0)
              setAdventurePage(0)
              setGroupPage(0)
              fetchAllStatistics()
              setDateRangeDialogOpen(false)
            }}
            disabled={statsLoading}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Settings Dialog */}
      <Dialog open={jobDialogOpen} onClose={() => setJobDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Statistics Rebuild Job</DialogTitle>
        <DialogContent>
          {jobLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : jobStatus ? (
            <Box sx={{ pt: 1 }}>
              {/* Job Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={jobStatus.isRunning ? 'Running' : 'Idle'}
                  color={jobStatus.isRunning ? 'success' : 'default'}
                  icon={jobStatus.isRunning ? <Schedule /> : undefined}
                />
                {jobStatus.isRunning && jobStatus.currentJob && (
                  <Typography variant="body2" color="text.secondary">
                    {jobStatus.currentJob.recordsProcessed} records processed
                  </Typography>
                )}
              </Box>

              {/* Current Job Progress */}
              {jobStatus.isRunning && jobStatus.currentJob && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Current Job
                  </Typography>
                  <Typography variant="body2">
                    Started: {formatDate(jobStatus.currentJob.startedAt)}
                  </Typography>
                  {jobStatus.currentJob.dateRangeStart && (
                    <Typography variant="body2">
                      Date Range: {jobStatus.currentJob.dateRangeStart} to {jobStatus.currentJob.dateRangeEnd}
                    </Typography>
                  )}
                  <LinearProgress sx={{ mt: 1 }} />
                </Box>
              )}

              {/* Last Completed Job */}
              {jobStatus.lastCompletedJob && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Last Completed Job
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed: {formatDate(jobStatus.lastCompletedJob.completedAt)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Duration: {jobStatus.lastCompletedJob.duration || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Records: {jobStatus.lastCompletedJob.recordsProcessed.toLocaleString()}
                  </Typography>
                  {jobStatus.lastCompletedJob.errorMessage && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {jobStatus.lastCompletedJob.errorMessage}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Data Summary */}
              {jobStatus.dataSummary && (
                <Box sx={{ mb: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Data Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Range: {jobStatus.dataSummary.earliestDate} to {jobStatus.dataSummary.latestDate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Users: {jobStatus.dataSummary.totalUserStatisticsDays} days |
                    Adventures: {jobStatus.dataSummary.totalAdventureStatisticsDays} days |
                    Groups: {jobStatus.dataSummary.totalGroupStatisticsDays} days
                  </Typography>
                </Box>
              )}

              {/* Rebuild Options */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Rebuild Options
              </Typography>
              <FormGroup row sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={includeUsers} onChange={(e) => setIncludeUsers(e.target.checked)} size="small" />}
                  label="Users"
                />
                <FormControlLabel
                  control={<Checkbox checked={includeAdventures} onChange={(e) => setIncludeAdventures(e.target.checked)} size="small" />}
                  label="Adventures"
                />
                <FormControlLabel
                  control={<Checkbox checked={includeGroups} onChange={(e) => setIncludeGroups(e.target.checked)} size="small" />}
                  label="Groups"
                />
              </FormGroup>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={rebuildStartDate}
                  onChange={(e) => setRebuildStartDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  value={rebuildEndDate}
                  onChange={(e) => setRebuildEndDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobDialogOpen(false)}>Close</Button>
          {jobStatus?.isRunning ? (
            <Button
              variant="contained"
              color="error"
              startIcon={stopping ? <CircularProgress size={16} color="inherit" /> : <Stop />}
              onClick={handleStopRebuild}
              disabled={stopping}
            >
              Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
              onClick={handleStartRebuild}
              disabled={starting}
            >
              Start Rebuild
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}
